import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { templateApi, runApi } from '../services/api';
import './OperationPortal.css';

const PAYSTACK_PUBLIC_KEY = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || 'pk_test_...'; // Use env var

const OperationPortal = () => {
    const { token, user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateDetail, setTemplateDetail] = useState(null);
    const [inputs, setInputs] = useState({});
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputSearchTerm, setInputSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [recentRuns, setRecentRuns] = useState([]);
    const [showSortModal, setShowSortModal] = useState(false);
    const [message, setMessage] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState({}); // { runId: 'success' }
    const [isPaying, setIsPaying] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        sortBy: [],
        groupBy: '',
        outputType: 'single_sheet_grouped',
        showSubtotals: true,
        showGrandTotal: true
    });

    const filteredTemplates = templates.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        fetchTemplates();
        fetchRecentRuns();

        // Load Paystack Script
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v1/inline.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const fetchRecentRuns = async () => {
        try {
            const data = await runApi.list({ limit: 5 });
            setRecentRuns(data.runs || []);
        } catch (err) {
            console.error('Failed to fetch recent runs:', err);
        }
    };

    const fetchTemplates = async () => {
        try {
            const data = await templateApi.list();
            setTemplates(data.templates || []);
        } catch (err) {
            console.error('Failed to fetch templates:', err);
        }
    };

    const handleTemplateChange = async (e) => {
        const id = e.target.value;
        setSelectedTemplateId(id);
        setResults(null);
        setError(null);

        if (!id) {
            setTemplateDetail(null);
            return;
        }

        try {
            const data = await templateApi.get(id);
            setTemplateDetail(data);

            const defs = typeof data.inputs_definition === 'string'
                ? JSON.parse(data.inputs_definition)
                : data.inputs_definition;

            // Initialize inputs
            const initialInputs = {};
            if (defs && defs.properties) {
                Object.keys(defs.properties).forEach(key => {
                    initialInputs[key] = '';
                });
            }
            setInputs(initialInputs);
        } catch (err) {
            setError('Could not load template details.');
        }
    };

    const handleInputChange = (key, value) => {
        setInputs(prev => ({ ...prev, [key]: value }));
    };

    const handlePayment = async (runId, outputKey, amount) => {
        setIsPaying(true);
        try {
            // 1. Initialize on backend
            const response = await fetch('/api/payments/initialize', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ runId, outputKey, amount, currency: 'GHS' })
            });

            const { data } = await response.json();

            // 2. Open Paystack Inline
            const handler = window.PaystackPop.setup({
                key: PAYSTACK_PUBLIC_KEY,
                email: user.email || `${user.username}@spreadsheetr.io`,
                amount: Math.round(amount * 100),
                currency: 'GHS',
                ref: data.reference,
                onClose: () => {
                    setIsPaying(false);
                    setMessage({ type: 'info', text: 'Payment cancelled' });
                },
                callback: (response) => {
                    setPaymentStatus(prev => ({ ...prev, [runId]: 'success' }));
                    setIsPaying(false);
                    setMessage({ type: 'success', text: 'Payment successful! Results unlocked.' });
                    fetchRecentRuns();
                }
            });
            handler.openIframe();
        } catch (err) {
            console.error('Payment initialization failed:', err);
            setError('Could not start payment process.');
            setIsPaying(false);
        }
    };

    const isPayable = (key) => {
        const payableKeywords = ['transcript', 'invoice', 'amortization', 'certificate', 'report'];
        return payableKeywords.some(kw => key.toLowerCase().includes(kw));
    };
    const handleRun = async () => {
        setLoading(true);
        setError(null);
        setResults(null);

        try {
            const formattedInputs = { ...inputs };
            const data = await runApi.execute(selectedTemplateId, formattedInputs);
            setResults(data.run);
            fetchRecentRuns();
        } catch (err) {
            setError(err.message || 'Execution Failed');
        } finally {
            setLoading(false);
        }
    };

    const getInputSchema = () => {
        if (!templateDetail?.inputs_definition) return { properties: {} };
        return typeof templateDetail.inputs_definition === 'string'
            ? JSON.parse(templateDetail.inputs_definition)
            : templateDetail.inputs_definition;
    };

    const inputSchema = getInputSchema();

    const handleQuickSort = () => {
        if (!results) return;
        setShowSortModal(true);
    };

    const handleExportSorted = async () => {
        if (!results) return;

        try {
            setLoading(true);
            setError(null);

            // Convert results.outputs object to array format for Excel
            // If outputs is like { totalFee: 5591600, studentCount: 500 }
            // We need to create a single row with these columns
            const dataArray = [results.outputs];

            // Prepare Excel configuration
            const excelConfig = {
                title: `${templateDetail.name} - Sorted Results`,
                sheetName: 'Results',
                outputType: sortConfig.outputType,
                sortBy: sortConfig.sortBy,
                groupBy: sortConfig.groupBy || undefined,
                showSectionHeaders: !!sortConfig.groupBy,
                showSubtotals: sortConfig.showSubtotals && !!sortConfig.groupBy,
                showGrandTotal: sortConfig.showGrandTotal,
                data: dataArray,
                columns: Object.keys(results.outputs).map(key => ({
                    key,
                    header: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
                    width: 15,
                    type: typeof results.outputs[key] === 'number' ? 'number' : 'text',
                    subtotal: typeof results.outputs[key] === 'number'
                }))
            };

            // Call export API
            const response = await fetch(`/api/formatting/export/excel/${results.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ config: excelConfig })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to export Excel');
            }

            // Download the file
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${templateDetail.name}-sorted-${results.id.slice(0, 8)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);

            setShowSortModal(false);
            setMessage({ type: 'success', text: 'Excel file exported successfully!' });

            // Clear message after 3 seconds
            setTimeout(() => setMessage(null), 3000);

        } catch (err) {
            console.error('Export error:', err);
            setError(err.message || 'Export failed');
        } finally {
            setLoading(false);
        }
    };

    const addSortColumn = () => {
        setSortConfig(prev => ({
            ...prev,
            sortBy: [...prev.sortBy, { column: '', order: 'asc' }]
        }));
    };

    const updateSortColumn = (index, field, value) => {
        setSortConfig(prev => ({
            ...prev,
            sortBy: prev.sortBy.map((sort, i) =>
                i === index ? { ...sort, [field]: value } : sort
            )
        }));
    };

    const removeSortColumn = (index) => {
        setSortConfig(prev => ({
            ...prev,
            sortBy: prev.sortBy.filter((_, i) => i !== index)
        }));
    };

    const getAvailableColumns = () => {
        if (!results?.outputs) return [];
        return Object.keys(results.outputs);
    };

    // Check if results contain multi-row data suitable for sorting
    const hasMultiRowData = () => {
        if (!results) return false;

        // Check if results have a 'data' array property (multi-row results)
        if (results.data && Array.isArray(results.data) && results.data.length > 1) {
            return true;
        }

        // Check if any output value is an array (multi-row results)
        if (results.outputs) {
            const values = Object.values(results.outputs);
            return values.some(val => Array.isArray(val) && val.length > 1);
        }

        return false;
    };

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    const handleAutoFill = () => {
        const schema = getInputSchema();
        if (!schema?.properties) return;

        const filledInputs = { ...inputs };
        let count = 0;

        Object.entries(schema.properties).forEach(([key, prop]) => {
            if (prop.example !== undefined && prop.example !== null) {
                filledInputs[key] = prop.example;
                count++;
            }
        });

        setInputs(filledInputs);

        // Auto-expand all groups to show the values
        const allGroups = {};
        Object.entries(groups).forEach(([name]) => {
            allGroups[name] = true;
        });
        setExpandedGroups(allGroups);

        setMessage({
            type: 'success',
            text: `Form refilled with ${count} original values!`
        });
        setTimeout(() => setMessage(null), 3000);
    };

    const getGroupedInputs = () => {
        const properties = inputSchema.properties || {};
        const groups = {};
        const ungrouped = [];

        Object.entries(properties).forEach(([key, schema]) => {
            const label = schema.description || key;

            // Check if it matches search term
            if (inputSearchTerm && !label.toLowerCase().includes(inputSearchTerm.toLowerCase())) {
                return;
            }

            // Regex to find "Label [N]" or "Label (N)" or "Label 1"
            const match = label.match(/^(.*?)\s*[\[\(\s]*\d+[\]\)\s]*$/) || label.match(/^(.*?)\s*\d+$/);

            if (match) {
                const groupName = match[1].trim();
                if (!groups[groupName]) groups[groupName] = [];
                groups[groupName].push({ key, schema, label });
            } else {
                ungrouped.push({ key, schema, label });
            }
        });

        return { groups, ungrouped };
    };

    const { groups, ungrouped } = getGroupedInputs();

    return (
        <div className="operation-portal">
            <header className="portal-header">
                <div className="portal-header-main">
                    <h1>{templateDetail ? (templateDetail.customTitle || templateDetail.name) : 'Calculation Portal'}</h1>
                    {templateDetail?.is_verified && (
                        <div className="integrity-seal" title="This template has passed a structural integrity audit.">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Certified Accurate
                        </div>
                    )}
                </div>
                <p className="portal-description">
                    {templateDetail ? templateDetail.description : 'Select a template and enter data to get your results.'}
                </p>
            </header>

            <div className="portal-grid">
                {/* Template Selection */}
                <section className="portal-card">
                    <div className="template-selection">
                        <div className="template-selection-row">
                            <div className="template-field">
                                <label>Search Template</label>
                                <input
                                    type="text"
                                    placeholder="Filter by name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="template-field">
                                <label>Select Template</label>
                                <select
                                    value={selectedTemplateId}
                                    onChange={handleTemplateChange}
                                >
                                    <option value="">-- Choose a Template --</option>
                                    {filteredTemplates.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} (v{t.version})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {templates.length === 0 && (
                            <div className="portal-warning">
                                <div className="portal-warning-content">
                                    <svg className="portal-warning-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                        <line x1="12" y1="9" x2="12" y2="13" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    <span className="portal-warning-text">No active templates found. Please contact an Admin.</span>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {templateDetail ? (
                    <>
                        {/* Input Form */}
                        <section className="portal-card">
                            <div className="portal-card-header">
                                <h2>Input Values</h2>
                                <div className="header-actions-group">
                                    <button
                                        type="button"
                                        className="btn-auto-fill"
                                        onClick={handleAutoFill}
                                        title="Auto-fill with original data from spreadsheet"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                                        </svg>
                                        <span>Auto-Fill</span>
                                    </button>
                                    <div className="input-search">
                                        <input
                                            type="text"
                                            placeholder="Search inputs..."
                                            value={inputSearchTerm}
                                            onChange={(e) => setInputSearchTerm(e.target.value)}
                                            className="search-input-field"
                                        />
                                    </div>
                                </div>
                            </div>
                            <form onSubmit={(e) => { e.preventDefault(); handleRun(); }} className="input-form">
                                <div className="grouped-inputs-container">
                                    {/* Ungrouped/Basic Inputs */}
                                    {ungrouped.length > 0 && (
                                        <div className="input-group-section ungrouped">
                                            <div className="input-grid">
                                                {ungrouped.map(({ key, schema, label }) => (
                                                    <div key={key} className="input-field">
                                                        <label>{label}</label>
                                                        <input
                                                            type={schema.type === 'number' ? 'number' : 'text'}
                                                            placeholder={`Enter ${label}...`}
                                                            value={inputs[key] !== undefined && inputs[key] !== null ? inputs[key] : ''}
                                                            onChange={(e) => handleInputChange(key, e.target.value === '' ? '' : (schema.type === 'number' ? Number(e.target.value) : e.target.value))}
                                                            required={inputSchema.required?.includes(key)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Grouped Inputs (e.g., Term Fee [1, 2, 3]) */}
                                    {Object.entries(groups).map(([groupName, fields]) => (
                                        <div key={groupName} className={`input-group-section ${expandedGroups[groupName] ? 'expanded' : 'collapsed'}`}>
                                            <div
                                                className="group-header"
                                                onClick={() => toggleGroup(groupName)}
                                            >
                                                <div className="group-title">
                                                    <svg className="chevron-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="6 9 12 15 18 9" />
                                                    </svg>
                                                    <h3>{groupName}</h3>
                                                    <span className="field-count">{fields.length} fields</span>
                                                </div>
                                            </div>
                                            <div className="group-content">
                                                <div className="input-grid compact">
                                                    {fields.map(({ key, schema, label }) => (
                                                        <div key={key} className="input-field">
                                                            <label>{label}</label>
                                                            <input
                                                                type={schema.type === 'number' ? 'number' : 'text'}
                                                                placeholder={`Value...`}
                                                                value={inputs[key] !== undefined && inputs[key] !== null ? inputs[key] : ''}
                                                                onChange={(e) => handleInputChange(key, e.target.value === '' ? '' : (schema.type === 'number' ? Number(e.target.value) : e.target.value))}
                                                                required={inputSchema.required?.includes(key)}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {ungrouped.length === 0 && Object.keys(groups).length === 0 && (
                                        <div className="no-inputs-found">
                                            No fields match your search "{inputSearchTerm}"
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="portal-error">
                                        <div className="portal-error-content">
                                            <svg className="portal-error-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="15" y1="9" x2="9" y2="15" />
                                                <line x1="9" y1="9" x2="15" y2="15" />
                                            </svg>
                                            <span className="portal-error-text">{error}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="calculate-button-container">
                                    <button
                                        type="submit"
                                        className="calculate-button"
                                        disabled={loading}
                                    >
                                        {loading ? 'Calculating...' : 'Calculate Results'}
                                    </button>
                                </div>
                            </form>
                        </section>

                        {/* Results */}
                        {results && (
                            <section className={`portal-card results-card theme-${templateDetail.themeVariant || templateDetail.purpose || 'general'}`}>
                                <div className="portal-card-header results-header">
                                    <h2>
                                        {templateDetail.customTitle || (
                                            templateDetail.purpose === 'analytical' ? 'Audit Report' :
                                                templateDetail.purpose === 'transactional' ? 'Billing Statement' :
                                                    templateDetail.purpose === 'official' ? 'Official Document' :
                                                        templateDetail.purpose === 'summary' ? 'Executive Summary' :
                                                            'Calculation Results'
                                        )}
                                    </h2>
                                    <span className="results-run-id">ID: {results.id.slice(0, 8)}</span>
                                </div>

                                <div className="results-table-container">
                                    <table className="results-table">
                                        <thead>
                                            <tr>
                                                <th>Output</th>
                                                <th style={{ textAlign: 'right' }}>Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(results.outputs || {}).map(([key, val]) => {
                                                const needsPayment = isPayable(key) && paymentStatus[results.id] !== 'success';

                                                // Find matching logic rule
                                                const ruleInfo = templateDetail?.logic_definition?.find(r => r.name === key || r.output?.name === key);

                                                return (
                                                    <tr key={key}>
                                                        <td className="output-name">
                                                            <div className="output-name-group">
                                                                {key}
                                                                {ruleInfo && (
                                                                    <div className="logic-hint tooltip" data-tooltip={ruleInfo.logic}>
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                                            <circle cx="12" cy="12" r="10" />
                                                                            <line x1="12" y1="16" x2="12" y2="12" />
                                                                            <line x1="12" y1="8" x2="12.01" y2="8" />
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {isPayable(key) && (
                                                                <span className={`payable-badge ${needsPayment ? 'locked' : 'unlocked'}`}>
                                                                    {needsPayment ? 'Premium' : 'Paid'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="output-value">
                                                            {needsPayment ? (
                                                                <button
                                                                    className="btn-pay-inline"
                                                                    onClick={() => handlePayment(results.id, key, 3.00)} // $3.00 flat fee
                                                                    disabled={isPaying}
                                                                >
                                                                    {isPaying ? '...' : 'Pay $3.00 to View'}
                                                                </button>
                                                            ) : (
                                                                typeof val === 'number'
                                                                    ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
                                                                    : String(val)
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="results-stats">
                                    <div className="results-stat">
                                        <div className="results-stat-label">Duration</div>
                                        <div className="results-stat-value">{results.duration || 'N/A'}</div>
                                    </div>
                                    <div className="results-stat">
                                        <div className="results-stat-label">Status</div>
                                        <div className="results-stat-value success">{results.status}</div>
                                    </div>
                                </div>

                                <div className="download-button-container">
                                    <button
                                        className="download-button"
                                        onClick={() => {
                                            const blob = new Blob([JSON.stringify({
                                                template: templateDetail.name,
                                                inputs,
                                                results: results.outputs,
                                                runId: results.id
                                            }, null, 2)], { type: 'application/json' });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `run-${results.id}.json`;
                                            a.click();
                                        }}
                                    >
                                        Download Result (JSON)
                                    </button>
                                    {hasMultiRowData() && (
                                        <button
                                            className="download-button download-excel"
                                            onClick={() => handleQuickSort()}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18M7 12h10M10 18h4" />
                                            </svg>
                                            Quick Sort & Export Excel
                                        </button>
                                    )}
                                </div>
                            </section>
                        )}
                    </>
                ) : (
                    <div className="portal-empty-state">
                        <div className="portal-empty-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2v11z" />
                            </svg>
                        </div>
                        <h3 className="portal-empty-title">Ready to Calculate</h3>
                        <p className="portal-empty-description">Pick a template from the list above to get started.</p>
                    </div>
                )}

                {/* Recent Runs */}
                <section className="recent-runs-section">
                    <h3 className="recent-runs-title">Recent Runs</h3>
                    <div className="recent-runs-list">
                        {recentRuns.length === 0 ? (
                            <div className="recent-runs-empty">
                                No recent history found.
                            </div>
                        ) : (
                            recentRuns.map(run => (
                                <div key={run.id} className="recent-run-item">
                                    <div className="recent-run-info">
                                        <div className={`recent-run-status ${run.status === 'failed' ? 'failed' : 'success'}`}>
                                            {run.status === 'failed' ? (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            ) : (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </div>
                                        <div className="recent-run-details">
                                            <div className="recent-run-id">Run {run.id.slice(0, 8)}</div>
                                            <div className="recent-run-date">{new Date(run.executedAt).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    <div className={`recent-run-status-text ${run.status === 'failed' ? 'failed' : 'success'}`}>
                                        {run.status}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>
            </div>

            {/* Quick Sort Modal */}
            {showSortModal && (
                <div className="modal-overlay" onClick={() => setShowSortModal(false)}>
                    <div className="modal-content sort-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18M7 12h10M10 18h4" />
                                </svg>
                                Quick Sort & Export
                            </h3>
                            <button
                                className="btn-close"
                                onClick={() => setShowSortModal(false)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="sort-section">
                                <label className="sort-label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="4" y1="6" x2="20" y2="6" />
                                        <line x1="4" y1="12" x2="20" y2="12" />
                                        <line x1="4" y1="18" x2="16" y2="18" />
                                    </svg>
                                    Sort Columns
                                </label>

                                {sortConfig.sortBy.map((sort, index) => (
                                    <div key={index} className="sort-row">
                                        <select
                                            value={sort.column}
                                            onChange={(e) => updateSortColumn(index, 'column', e.target.value)}
                                            className="sort-select"
                                        >
                                            <option value="">Select column...</option>
                                            {getAvailableColumns().map(col => (
                                                <option key={col} value={col}>{col}</option>
                                            ))}
                                        </select>

                                        <select
                                            value={sort.order}
                                            onChange={(e) => updateSortColumn(index, 'order', e.target.value)}
                                            className="sort-order"
                                        >
                                            <option value="asc">Ascending</option>
                                            <option value="desc">Descending</option>
                                        </select>

                                        <button
                                            className="btn-remove"
                                            onClick={() => removeSortColumn(index)}
                                            title="Remove"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}

                                <button
                                    className="btn-add-sort"
                                    onClick={addSortColumn}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add Sort Column
                                </button>
                            </div>

                            <div className="sort-section">
                                <label className="sort-label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="3" width="7" height="7" />
                                        <rect x="14" y="3" width="7" height="7" />
                                        <rect x="14" y="14" width="7" height="7" />
                                        <rect x="3" y="14" width="7" height="7" />
                                    </svg>
                                    Group By (Optional)
                                </label>
                                <select
                                    value={sortConfig.groupBy}
                                    onChange={(e) => setSortConfig(prev => ({ ...prev, groupBy: e.target.value }))}
                                    className="sort-select"
                                >
                                    <option value="">No grouping</option>
                                    {getAvailableColumns().map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="sort-section">
                                <label className="sort-label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    Output Type
                                </label>
                                <select
                                    value={sortConfig.outputType}
                                    onChange={(e) => setSortConfig(prev => ({ ...prev, outputType: e.target.value }))}
                                    className="sort-select"
                                    disabled={!sortConfig.groupBy}
                                >
                                    <option value="single_sheet_grouped">Single Sheet (Grouped Sections)</option>
                                    <option value="separate_sheets">Separate Sheets (One per Group)</option>
                                    <option value="simple_sorted">Simple Sorted (No Grouping)</option>
                                </select>
                            </div>

                            {sortConfig.groupBy && (
                                <div className="sort-options">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={sortConfig.showSubtotals}
                                            onChange={(e) => setSortConfig(prev => ({ ...prev, showSubtotals: e.target.checked }))}
                                        />
                                        <span>Show Subtotals per Group</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={sortConfig.showGrandTotal}
                                            onChange={(e) => setSortConfig(prev => ({ ...prev, showGrandTotal: e.target.checked }))}
                                        />
                                        <span>Show Grand Total</span>
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowSortModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleExportSorted}
                                disabled={loading || sortConfig.sortBy.length === 0}
                            >
                                {loading ? 'Exporting...' : 'Export Excel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OperationPortal;
